/**
 * Zentraler, app-weiter Store für die KONFIGURIERBAREN Outcome-Anzeigenamen
 * (Meeting M1–M8 + FU F1–F9), gepflegt unter Vertriebseinstellungen → Referenzen
 * (`_OUTCOME_CODES`/`_FU_OUTCOME_CODES`). Einmal beim App-Start aus dem Endpoint
 * `/api/v1/app/outcome-labels` befüllt (siehe main.ts, analog DATE_FORMAT) und
 * von allen Views via `outcomeLabel()` gelesen — damit ein Umbenennen überall
 * durchschlägt, statt in jeder View hartkodiert zu sein.
 *
 * Granularität: konfigurierbar sind die BASIS-Codes (M1–M8 / F1–F9). Die
 * Vertriebshistorie kennt Feinvarianten (M7-A/M7-B, F7-A/F7-B = Absage mit/ohne
 * Grund); deren Anzeige wird aus dem konfigurierten Basis-Label abgeleitet
 * („<Basis> (mit Grund)"). System-Events (Zyklus-Start, Lead-Status-Wechsel,
 * Closing …) sind KEINE Outcomes und bleiben außerhalb dieses Stores.
 *
 * Fail-safe: solange nichts geladen ist (oder ein Code fehlt), liefert
 * `outcomeLabel(code, fallback)` den übergebenen Default — die Views behalten
 * also ihre bisherigen Texte, bis der Store da ist / falls der Endpoint scheitert.
 */

let CONFIGURED: Record<string, string> = {};

/** Setzt die globale code→label-Map (aus dem App-Start-Endpoint). */
export function setOutcomeLabels(map: Record<string, string> | null | undefined): void {
  CONFIGURED = map && typeof map === "object" ? { ...map } : {};
}

/** Aktuelle konfigurierte Map (read-only Kopie) — v.a. für Tests/Debug. */
export function getOutcomeLabels(): Record<string, string> {
  return { ...CONFIGURED };
}

/** Feinvariante → Basis-Code + Zusatz; das Anzeige-Label leitet sich aus dem Basis ab. */
const GRANULAR: Record<string, { base: string; suffix: string }> = {
  "M7-A": { base: "M7", suffix: "mit Grund" },
  "M7-B": { base: "M7", suffix: "ohne Grund" },
  "F7-A": { base: "F7", suffix: "mit Grund" },
  "F7-B": { base: "F7", suffix: "ohne Grund" }
};

/**
 * Anzeigename für einen Outcome-Code.
 *   - Basis-Code (M1–M8/F1–F9): konfiguriertes Label, sonst `fallback`/Code.
 *   - Feinvariante (M7-A/B, F7-A/B): „<konfiguriertes Basis-Label> (mit/ohne Grund)";
 *     fehlt das Basis-Label, `fallback`/Code (z.B. der hartkodierte Granular-Text).
 *   - Unbekannter Code (System-Events etc.): `fallback`/Code unverändert.
 */
export function outcomeLabel(code: string, fallback?: string): string {
  const c = (code ?? "").trim();
  if (!c) return fallback ?? "";
  const g = GRANULAR[c];
  if (g) {
    const base = CONFIGURED[g.base];
    return base ? `${base} (${g.suffix})` : (fallback ?? c);
  }
  return CONFIGURED[c] ?? fallback ?? c;
}
